import React from 'react';
import { CssBaseline, PaletteMode, ThemeProvider, createTheme } from '@mui/material';

type ColorSchemePreference = 'light' | 'dark' | 'system';

type AppearanceContextValue = {
  mode: PaletteMode;
  setMode: (mode: PaletteMode) => void;
  /** The user's preference (light, dark, or system) */
  preference: ColorSchemePreference;
  /** Set the color scheme preference */
  setPreference: (pref: ColorSchemePreference) => void;
  /** Whether the current mode is determined by system preference */
  isSystemMode: boolean;
};

const AppearanceContext = React.createContext<AppearanceContextValue | null>(null);

export const useAppearance = (): AppearanceContextValue => {
  const ctx = React.useContext(AppearanceContext);
  if (!ctx) throw new Error('useAppearance must be used within AppearanceProvider');
  return ctx;
};

const STORAGE_KEY = 'pw-mode';
const PREFERENCE_KEY = 'pw-color-scheme-preference';

/**
 * Get the system's preferred color scheme
 */
const getSystemPreference = (): PaletteMode => {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

/**
 * Get the stored preference (light, dark, or system)
 */
const getStoredPreference = (): ColorSchemePreference => {
  if (typeof window === 'undefined') return 'system';
  const stored = window.localStorage.getItem(PREFERENCE_KEY) as ColorSchemePreference | null;
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;

  // Migration: check for old storage key format
  const legacyStored = window.localStorage.getItem(STORAGE_KEY) as PaletteMode | null;
  if (legacyStored === 'light' || legacyStored === 'dark') {
    // Migrate to new preference system
    window.localStorage.setItem(PREFERENCE_KEY, legacyStored);
    return legacyStored;
  }

  // Default to system preference
  return 'system';
};

const getStoredMode = (): PaletteMode => {
  if (typeof window === 'undefined') return 'dark';
  const preference = getStoredPreference();
  if (preference === 'system') {
    return getSystemPreference();
  }
  return preference;
};

const setStoredMode = (mode: PaletteMode) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
};

// Minimal two-theme design: light = slate, dark = denim
const buildTheme = (mode: PaletteMode) =>
  createTheme({
    palette: {
      mode,
      // Stronger denim accent in dark mode, subtle slate in light mode
      primary: { main: mode === 'dark' ? '#1E63E9' : '#4B5563' },
      secondary: { main: mode === 'dark' ? '#7AA7FF' : '#6B7280' },
      background: {
        default: mode === 'dark' ? '#0f1115' : '#f7f8fa',
        paper: mode === 'dark' ? '#141821' : '#ffffff',
      },
      text: {
        primary: mode === 'dark' ? '#E6ECF5' : '#111827',
        secondary: mode === 'dark' ? '#9AA8BF' : '#6B7280',
      },
      divider: mode === 'dark' ? '#2B3340' : '#E5E7EB',
      action: {
        // Used for selected header bg etc.; slightly blue-tinted in dark mode
        selected: mode === 'dark' ? 'rgba(30, 99, 233, 0.18)' : 'rgba(0,0,0,0.06)',
      },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            // Ensure readable text on dark backgrounds
            color: mode === 'dark' ? '#E6ECF5' : undefined,
          },
        },
      },
    },
  });

export const AppearanceProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [preference, setPreferenceState] = React.useState<ColorSchemePreference>(getStoredPreference);
  const [mode, setModeState] = React.useState<PaletteMode>(getStoredMode());

  // Set preference and persist to localStorage
  const setPreference = React.useCallback((pref: ColorSchemePreference) => {
    setPreferenceState(pref);
    try {
      window.localStorage.setItem(PREFERENCE_KEY, pref);
      // Update mode based on new preference
      const newMode = pref === 'system' ? getSystemPreference() : pref;
      setModeState(newMode);
      setStoredMode(newMode);
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Direct mode setter (for backwards compatibility)
  const setMode = React.useCallback((newMode: PaletteMode) => {
    setModeState(newMode);
    setPreferenceState(newMode); // Explicit mode means not using system
    try {
      window.localStorage.setItem(PREFERENCE_KEY, newMode);
      setStoredMode(newMode);
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Listen for system preference changes when in 'system' mode
  React.useEffect(() => {
    if (preference !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      const newMode = e.matches ? 'dark' : 'light';
      setModeState(newMode);
      setStoredMode(newMode);
      document.documentElement.dataset.theme = newMode;
    };

    // Set initial value based on system preference
    const systemMode = mediaQuery.matches ? 'dark' : 'light';
    if (mode !== systemMode) {
      setModeState(systemMode);
      setStoredMode(systemMode);
    }

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [preference, mode]);

  // Update document theme attribute
  React.useEffect(() => {
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  const theme = React.useMemo(() => buildTheme(mode), [mode]);

  const isSystemMode = preference === 'system';

  const value = React.useMemo(
    () => ({ mode, setMode, preference, setPreference, isSystemMode }),
    [mode, setMode, preference, setPreference, isSystemMode],
  );

  return (
    <AppearanceContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </AppearanceContext.Provider>
  );
};
